import i18n, { type TOptions } from "i18next";
import { initReactI18next, useTranslation as useTranslationBase } from "react-i18next";
import HttpBackend from "i18next-http-backend";
import type { TranslatedString } from "@Helpers/types/i18n";
import enJson from "../../public/locales/en.json";

// Re-export types from helpers
export type { TranslatedString } from "@Helpers/types/i18n";
export { untranslated } from "@Helpers/types/i18n";

// Re-export react-i18next primitives so the whole app imports them from "@i18n".
export { Trans, withTranslation } from "react-i18next";

/**
 * Union type of all valid translation keys derived from en.json.
 * TypeScript will error if you pass a key that doesn't exist in en.json.
 */
export type TranslationKey = keyof typeof enJson;

/**
 * Module augmentation to make i18next's t() only accept valid keys from en.json and return
 * TranslatedString.
 *
 * `CustomTypeOptions.resources` narrows i18next's native TFunction so that any key NOT present in en.json
 * is a compile-time error. `keySeparator`/`nsSeparator` are disabled so flat sentence keys containing "."
 * or ":" are treated as literal keys. The `TFunction` call-signature overload brands the return value as
 * TranslatedString — needed because react-i18next v17 exports `useTranslation` as a const, which cannot be
 * augmented as a function.
 */
declare module "i18next" {
  interface TFunction {
    (key: TranslationKey | TranslationKey[], options?: Record<string, unknown>): TranslatedString;
  }
}

/**
 * Strictly-typed `useTranslation` hook. Keys are validated against en.json at compile
 * time and `t()` returns a branded TranslatedString. App code must import this from
 * "@i18n" (react-i18next v17 exports `useTranslation` as a const, which cannot be
 * augmented as a function — so we re-type the same hook here instead).
 */
type TypedTranslation = {
  t: (key: TranslationKey | TranslationKey[], options?: Record<string, unknown>) => TranslatedString;
  i18n: typeof i18n;
};

export const useTranslation = (): TypedTranslation => {
  const { t, i18n: i18nInstance } = useTranslationBase();
  return { t, i18n: i18nInstance };
};

// Detect whether it's running in React Native
const isReactNative = typeof navigator !== "undefined" && navigator.product === "ReactNative";

const getPreferredLanguage = () => {
  if (isReactNative) {
    return "en";
  }
  // lib.dom declares `globalThis.localStorage` as always present, but this module is also loaded in
  // non-DOM runtimes (Node during tests / SSR) where it is absent. Annotating a `const` as
  // `Storage | undefined` does not help — the initializer narrows it straight back to `Storage` — so
  // it is feature-detected the same way `isReactNative` above detects `navigator`.
  if (typeof globalThis.localStorage === "undefined") {
    return "en";
  }
  return globalThis.localStorage.getItem("preferredLanguage") ?? "en";
};

// Module-init side effect: the app renders through `useSuspense`, which waits on the backend load
// itself, so nothing here has a promise to hand back. A failed init is left unhandled on purpose —
// that is what reports it to Sentry as `handled: false`.
void i18n
  .use(HttpBackend)
  .use(initReactI18next)
  .init({
    fallbackLng: getPreferredLanguage(),
    backend: {
      loadPath: "/locales/{{lng}}.json",
    },
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: !isReactNative,
      transKeepBasicHtmlNodesFor: ["br", "strong", "b", "i", "em", "p", "u", "span"],
    },
    defaultNS: "common",
    ns: [],
  });

/**
 * Typed translation function that returns a TranslatedString.
 * Use this function to ensure toast notification content is properly internationalized.
 *
 * @example
 * import { t } from "@i18n";
 * dispatch(showToastNotification({ content: t("Something went wrong!") }));
 */
export function t(key: TranslationKey, options?: TOptions): TranslatedString {
  return i18n.t(key, options) as TranslatedString;
}

export default i18n;
